/**
 * useGate — Single hook for all freemium gating
 * Replaces useFreeTrial + useFeatureLimits
 *
 * Usage:
 *   const { checkAccess, peekUsage, gateState, closeGate, GateModalComponent } = useGate();
 *
 *   const handleCalculate = () => {
 *     if (!checkAccess('quick_estimate')) return;
 *     // ... proceed with calculation
 *   };
 *
 *   // Show usage info without blocking
 *   const usage = peekUsage('material_estimate');
 *   // usage = { used: 1, limit: 5, remaining: 4, isUnlimited: false, isBlocked: false }
 *
 *   return <>{content}<GateModalComponent /></>;
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from '../utils/authUtils';
import { useSubscription } from '../contexts/SubscriptionContext';
import { isAuthenticated as apiIsAuthenticated } from '../utils/api';
import {
  evaluateGate,
  incrementGuestUsage,
  GATE_REASONS,
} from '../constants/gateConfig';
import GateModal from '../components/gate/GateModal';

export const useGate = () => {
  const { user } = useAuth();
  const subscription = useSubscription();
  const [gateState, setGateState] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const isAuthenticated = !!user || apiIsAuthenticated();

  /**
   * Check if an action is allowed. If not, auto-opens the gate modal.
   * @param {string} actionCode - from GATE_ACTIONS
   * @returns {boolean} true if allowed, false if blocked (modal opened)
   */
  const checkAccess = useCallback((actionCode) => {
    // While context is loading, optimistically allow (will re-check server-side)
    if (subscription.loading && isAuthenticated) {
      return true;
    }

    const result = evaluateGate(actionCode, {
      isAuthenticated,
      isPassActive: subscription.hasActivePass,
      serverActionLimits: subscription.actionLimits || null,
    });

    if (!result.allowed) {
      setGateState({
        reason: result.reason,
        actionCode,
        used: result.used,
        limit: result.limit,
        remaining: result.remaining,
        minimumPass: result.minimumPass,
      });
      setIsOpen(true);
      return false;
    }

    // Allowed — record usage
    if (!isAuthenticated) {
      // Guest: increment local counter
      incrementGuestUsage(actionCode);
    } else {
      // Logged-in: record via API (fire-and-forget, don't block the action)
      subscription.recordFeatureUsage(actionCode).catch(() => {});
    }

    return true;
  }, [isAuthenticated, subscription]);

  /**
   * Peek at usage for an action without blocking or opening modal.
   * Useful for displaying usage alerts/banners.
   * @param {string} actionCode - from GATE_ACTIONS
   * @returns {{ used: number, limit: number, remaining: number, isUnlimited: boolean, isBlocked: boolean }}
   */
  const peekUsage = useCallback((actionCode) => {
    if (subscription.loading && isAuthenticated) {
      return { used: 0, limit: -1, remaining: -1, isUnlimited: true, isBlocked: false };
    }

    const result = evaluateGate(actionCode, {
      isAuthenticated,
      isPassActive: subscription.hasActivePass,
      serverActionLimits: subscription.actionLimits || null,
    });

    return {
      used: result.used || 0,
      limit: result.limit,
      remaining: result.remaining,
      isUnlimited: result.limit === -1,
      isBlocked: !result.allowed,
    };
  }, [isAuthenticated, subscription]);

  /**
   * Close the gate modal
   */
  const closeGate = useCallback(() => {
    setIsOpen(false);
    setGateState(null);
  }, []);

  /**
   * Pre-wired GateModal component — just render it at the bottom of your JSX
   */
  const GateModalComponent = useCallback(() => (
    <GateModal open={isOpen} onClose={closeGate} gateState={gateState} />
  ), [isOpen, closeGate, gateState]);

  return {
    checkAccess,
    peekUsage,
    gateState,
    isGateOpen: isOpen,
    closeGate,
    GateModalComponent,
    isAuthenticated,
  };
};

export default useGate;
